/**
 * VerifyOTPStep - Step 2: Enter 6-digit OTP code sent to email.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Alert, Button, Stack, alpha, Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import MarkEmailReadRounded from '@mui/icons-material/MarkEmailReadRounded';
import { brandPalette } from '../../../theme/brandPalette';
import OnboardingLayout from '../components/OnboardingLayout';
import { CodeField } from '../../../ui-kit';

const RESEND_COOLDOWN = 60;

interface VerifyOTPStepProps {
  email: string;
  onVerify: (otpCode: string) => Promise<any>;
  onResend: () => Promise<any>;
  /** Return to the email step (e.g. wrong address). */
  onBackToEmail?: () => void;
  loading: boolean;
  error: string | null;
}

export default function VerifyOTPStep({ email, onVerify, onResend, onBackToEmail, loading, error }: VerifyOTPStepProps) {
  const [code, setCode] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  // Forward `?redirect=` (set by auth guards on shared deep links) when the
  // user switches to the password sign-in flow, so the target survives.
  const [searchParams] = useSearchParams();
  const passwordLoginHref = (() => {
    const r = searchParams.get('redirect');
    return r ? `/login-pwd?redirect=${encodeURIComponent(r)}` : '/login-pwd';
  })();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Avoids double verify (6th digit auto-submit + Enter on form + clicking the button). */
  const verifyStartedRef = useRef(false);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startCooldown = useCallback(() => {
    setCooldown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const runVerify = useCallback(
    async (code: string) => {
      if (verifyStartedRef.current || loading || code.length !== 6) return;
      verifyStartedRef.current = true;
      try {
        await onVerify(code);
      } finally {
        verifyStartedRef.current = false;
      }
    },
    [loading, onVerify],
  );

  const handleResend = async () => {
    setResending(true);
    setResendMsg(null);
    try {
      const result = await onResend();
      setResendMsg(`A new code was sent to ${email}`);
      setCode('');
      startCooldown();
      if (result?.resend_count && result?.max_resends) {
        setResendMsg(`Code resent - ${result.max_resends - result.resend_count} attempt${result.max_resends - result.resend_count !== 1 ? 's' : ''} remaining`);
      }
    } catch {
      // Error handled by parent
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length === 6) void runVerify(code);
  };

  const allFilled = code.length === 6;

  return (
    <OnboardingLayout maxWidth={480} stepLabel="Step 2 of 3">
      <Card sx={{ borderRadius: 3, boxShadow: `0 4px 24px ${alpha(brandPalette.primary[400], 0.08)}` }}>
        <CardContent sx={{ p: { xs: 3, md: 4.5 }, textAlign: 'center' }}>

          {/* Icon */}
          <Box sx={{
            width: 52, height: 52, borderRadius: '14px',
            bgcolor: brandPalette.primary[100],
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2.5,
          }}>
            <MarkEmailReadRounded sx={{ color: brandPalette.primary[400], fontSize: 26 }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, letterSpacing: -0.5 }}>
            Check your inbox
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            We sent a 6-digit verification code to
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: brandPalette.primary[400], mb: 3.5 }}>
            {email}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5, textAlign: 'left', borderRadius: 2 }}>{error}</Alert>}
          {resendMsg && <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>{resendMsg}</Alert>}

          <form onSubmit={handleSubmit} noValidate>
            <Box sx={{ mb: 4 }}>
              <CodeField
                value={code}
                onChange={setCode}
                onComplete={(full) => { void runVerify(full); }}
                error={Boolean(error)}
                disabled={loading}
                autoFocus
              />
            </Box>

            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              loading={loading}
              size="large"
              disabled={!allFilled}
              sx={{
                py: 1.5, borderRadius: 2, fontWeight: 700,
                boxShadow: allFilled ? `0 4px 14px ${alpha(brandPalette.primary[400], 0.35)}` : undefined,
                transition: 'box-shadow 0.2s',
              }}
            >
              Verify & continue
            </LoadingButton>
          </form>

          {/* Resend section */}
          <Box sx={{ mt: 3.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Didn't get the code? Check your spam folder or resend.
            </Typography>
            {cooldown > 0 ? (
              <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
                Resend available in {cooldown}s
              </Typography>
            ) : (
              <Button
                variant="text"
                size="small"
                onClick={handleResend}
                disabled={resending || loading}
                sx={{ color: brandPalette.primary[400], fontWeight: 600, textTransform: 'none' }}
              >
                {resending ? 'Sending…' : 'Resend the code'}
              </Button>
            )}
          </Box>

          <Stack spacing={1} sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {onBackToEmail && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => onBackToEmail()}
                  underline="hover"
                  sx={{
                    fontWeight: 600,
                    color: brandPalette.primary[400],
                    cursor: 'pointer',
                    border: 'none',
                    background: 'none',
                    font: 'inherit',
                    p: 0,
                  }}
                >
                  Use a different email
                </Link>
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to={passwordLoginHref}
                underline="hover"
                sx={{ fontWeight: 600, color: brandPalette.primary[400] }}
              >
                Log in with password instead
              </Link>
            </Typography>
          </Stack>

        </CardContent>
      </Card>
    </OnboardingLayout>
  );
}
